import React from 'react';
import { useTranslation } from 'react-i18next';

import { IChatMessageType } from '@fly/shared';
import {
  FlyAILoader,
  FlyMarkdownRenderer,
  FlyTextLoading,
} from '@fly/shared-ui';
import { selectThreadMessageById, useAppSelector } from '@fly/store';
import { FlySvgSprite } from '@fly/svg';

import styles from '../../FlyChat.module.less';
import { FlyChatStreamingSteps } from '../FlyChatStreamingSteps/FlyChatStreamingSteps';
import { FlyMessageActions } from '../FlyMessageActions/FlyMessageActions';

export const FlyChatMessage = React.memo(function ({
  messageId,
  threadId,
}: {
  messageId: string;
  threadId?: string;
}) {
  const { t } = useTranslation();
  const message = useAppSelector((state) =>
    threadId ? selectThreadMessageById(state, threadId, messageId) : null
  );

  if (!message) {
    return null;
  }

  const isAiMessage = message.type === IChatMessageType.AI;
  const hasContent = message.content.length > 0;
  const streamingSteps = message.streamingSteps ?? [];
  const showStreamingUi = isAiMessage && message.isStreaming;
  const hasActiveStep = streamingSteps.some((step) => step.status === 'active');
  const showThinkingLoader =
    showStreamingUi &&
    ((!hasContent && streamingSteps.length === 0) ||
      (hasContent && hasActiveStep));

  return (
    <div
      data-testid='chat-message'
      className={`${styles.message} ${
        message.type === IChatMessageType.HUMAN
          ? styles.userMessage
          : styles.aiMessage
      }`}
    >
      <div className={styles.messageContent}>
        {isAiMessage && (hasContent || showStreamingUi) && (
          <FlySvgSprite name='ai' size={20} />
        )}
        <div className={styles.messageContainer}>
          {hasContent && <FlyMarkdownRenderer markdown={message.content} />}
          {showStreamingUi && streamingSteps.length > 0 && (
            <FlyChatStreamingSteps steps={streamingSteps} />
          )}
          {showThinkingLoader && (
            <FlyAILoader
              data-testid='chat-thinking-loader'
              className={`${styles.streamingThinkingLoader} mr-2 h-[18px] w-[18px] stroke-(--fly-color-scheme-primary-5) text-(--fly-color-scheme-primary-6)`}
            >
              <FlyTextLoading
                textSize='1'
                text={t('app.dashboard.chat.content.thinking', {
                  defaultValue: 'Thinking',
                })}
              />
            </FlyAILoader>
          )}
        </div>
      </div>
      {isAiMessage && hasContent && !message.isStreaming && (
        <FlyMessageActions message={message} />
      )}
    </div>
  );
});

FlyChatMessage.displayName = 'FlyChatMessage';
